/**
 * Standalone Algolia search utility for direct index queries
 * without the default filters applied by the main search system.
 *
 * Use this for specific use cases that need plain text searches
 * across specific indexes without date filters or hasPublishedEvents facets.
 */

import axios from 'redaxios'
import { config } from '../constants'

interface AlgoliaSearchOptions {
  indexes: string[]
  query: string
  hitsPerPage?: number
}

interface AlgoliaSearchResults {
  [indexName: string]: {
    items: any[]
    total: number
    page: number
    numberOfPages: number
  }
}

/**
 * Performs a direct multi-index search against Algolia without any default filters.
 * This is useful for autocomplete/typeahead searches where you want plain text matching.
 *
 * @param options - Search options including indexes to query and the search query
 * @returns Results keyed by index name
 */
export async function searchAlgoliaIndexes(
  options: AlgoliaSearchOptions
): Promise<AlgoliaSearchResults> {
  const { indexes, query, hitsPerPage = 30 } = options

  const algoliaAppId = config.algolia.appId
  const algoliaApiKey = import.meta.env.VITE_ALGOLIA_API_KEY || ''
  const url = `https://${algoliaAppId}-dsn.algolia.net/1/indexes/*/queries`

  const requestConfig = {
    headers: {
      'X-Algolia-API-Key': algoliaApiKey,
      'X-Algolia-Application-Id': algoliaAppId,
      'Content-Type': 'application/json',
    },
  }

  // Build requests for each index - plain query with no filters
  const requests = indexes.map(indexName => ({
    indexName,
    params: `query=${encodeURIComponent(query)}&hitsPerPage=${hitsPerPage}`,
  }))

  try {
    const response = await axios.post(url, { requests }, requestConfig)
    const results: AlgoliaSearchResults = {}

    if (response.data?.results) {
      for (const result of response.data.results) {
        results[result.index] = {
          items: result.hits || [],
          total: result.nbHits || 0,
          page: result.page || 0,
          numberOfPages: result.nbPages || 0,
        }
      }
    }

    return results
  } catch (error) {
    console.error('Algolia direct search error:', error)
    return {}
  }
}
